import React from "react";
import { useParams } from "react-router-dom";
import { useBackgroundJob } from "../../features/background-jobs/hooks/useBackgroundJob";
import { BackgroundJobResults } from "../../features/background-jobs/queries/backgroundJobs";
import { Button, Header, Segment } from "semantic-ui-react";
import PlaceholderUntilLoaded from "../../components/global_components/PlaceholderUntilLoaded";
import JobProgress from "../../features/background-jobs/components/JobProgress";

const downloadResults = async (results: BackgroundJobResults) => {
  const { signedUrl } = results;
  const file = await fetch(signedUrl);
  const fileAsArrayBuffer = await file.arrayBuffer();
  const blob = new Blob([fileAsArrayBuffer], { type: "text/csv;charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl);
};

export const CrmExportJobProgress = () => {
  const { id } = useParams<{ id: string }>();
  const {
    backgroundJob,
    backgroundJobError,
    isBackgroundJobLoading,
    isBackgroundJobErrored: isBackgroundJobQueryErrored,
    backgroundJobResults,
    backgroundJobResultsError,
    isBackgroundJobResultsLoading,
    isBackgroundJobResultsErrored,
    cancelJobMutation,
    isCancelJobMutationLoading,
  } = useBackgroundJob(parseInt(id), {
    refetchInterval: 1000,
    getJobResultsWhenDone: true,
    toastOnError: false,
    onJobResultsFinished: (job, results) => {
      if (job.status === "Completed" && results) {
        downloadResults(results);
      }
    },
  });
  const backgroundJobStatus = backgroundJob?.status;

  const backgroundJobErrored = backgroundJobStatus === "Errored";
  const backgroundJobCancelled = backgroundJobStatus === "Cancelled";
  const backgroundJobCompleted = backgroundJobStatus === "Completed";
  const backgroundJobDone = backgroundJobErrored || backgroundJobCancelled || backgroundJobCompleted;
  const isError = isBackgroundJobQueryErrored || backgroundJobErrored || isBackgroundJobResultsErrored;
  const { header, subheader } = (() => {
    if (isError) {
      return {
        header: "Generating Export Failed",
        subheader: "An error occurred while generating your export",
      };
    }
    if (isCancelJobMutationLoading) {
      return {
        header: "Cancelling Export...",
        subheader: "Please wait while we cancel your export",
      };
    }
    if (backgroundJobCancelled) {
      return {
        header: "Export Cancelled",
        subheader: "Your export has been cancelled",
      };
    }
    if (backgroundJobCompleted) {
      return {
        header: "Export Completed",
        subheader:
          "Your export has been successfully generated, click download results if it does not start automatically",
      };
    }
    return {
      header: "Generating Export...",
      subheader: "Please wait while we generate your export",
    };
  })();

  const jobError = (() => {
    if (backgroundJob?.jobError) {
      return backgroundJob.jobError;
    }
    if (isBackgroundJobQueryErrored) {
      if (backgroundJobError) {
        return {
          name: backgroundJobError.title + " - " + backgroundJobError.subHeader,
          message: backgroundJobError.message,
        };
      }
      return {
        name: "Error",
        message: "An error occurred while fetching the background job",
      };
    }
    if (isBackgroundJobResultsErrored) {
      if (backgroundJobResultsError) {
        return {
          name: backgroundJobResultsError.title + " - " + backgroundJobResultsError.subHeader,
          message: backgroundJobResultsError.message,
        };
      }
      return {
        name: "Error",
        message: "An error occurred while fetching the background job results",
      };
    }
  })();
  return (
    <div className='tw-flex tw-justify-center tw-p-16'>
      <Segment className='tw-w-full tw-max-w-4xl' color={isError ? "red" : undefined}>
        <Header size='large'>
          {header}
          <Header.Subheader>{subheader}</Header.Subheader>
        </Header>
        {((!backgroundJobCancelled && !backgroundJobCompleted) || !!jobError) && (
          <PlaceholderUntilLoaded isLoading={isBackgroundJobLoading} lineCount={5}>
            <JobProgress
              title={backgroundJob?.name ?? "Loading..."}
              currentRecords={backgroundJob?.recordsProcessed}
              totalRecords={backgroundJob?.recordsTotal}
              isTest={backgroundJob?.isTest}
              currentStep={backgroundJob?.stepCurrent}
              totalSteps={backgroundJob?.stepTotal}
              stepProgressMessage={backgroundJob?.stepProgressMessage}
              progress={backgroundJob?.progressPercentage}
              errorCount={backgroundJob?.errorCount}
              cancelJob={cancelJobMutation}
              isCancelling={isCancelJobMutationLoading}
              jobError={jobError}
            />
          </PlaceholderUntilLoaded>
        )}
        {backgroundJobDone && !isError && !backgroundJobCancelled && (
          <Button
            className='tertiary'
            onClick={() => {
              if (backgroundJobResults) {
                downloadResults(backgroundJobResults);
              }
            }}
            loading={isBackgroundJobResultsLoading}
            disabled={isBackgroundJobResultsErrored || isBackgroundJobResultsLoading}
          >
            Click here if your file is not downloading
          </Button>
        )}
      </Segment>
    </div>
  );
};
